
import { GameStatus } from "@/lib/types/GameTypes";
import { setCookie } from "@/lib/cookieUtils";
import { streakManager } from "./StreakManager";
import { formatDate, getEffectiveDate } from "@/lib/dateUtils";
import { getDebugParams } from "@/lib/debugUtils";
import { clearGameState } from "../GamePersistenceV2";
import * as CookieManager from "@/lib/managers/CookieManager";
import { CompletionStateManager } from './CompletionStateManager';
import { modeAwareStorage } from '@/lib/managers/ModeAwareStorageManager';
import { atomicGameStorage } from '@/lib/storage/AtomicGameStorage';

// Centralized status-event logger for debug clipboard
const logStatusEvent = (label: string, details: Record<string, any> = {}) => {
  try {
    const page = typeof window !== 'undefined' ? window.location.pathname : 'server';
    const mode = typeof window !== 'undefined' ? (localStorage.getItem('babblegram_game_mode') || 'daily') : 'unknown';
    console.log(`📝 STATUS_EVENT - ${label}`, {
      page,
      mode,
      ...details,
      timestamp: new Date().toISOString()
    });
  } catch {}
};

// Utility function to validate game status
const validateGameStatus = (status: any): GameStatus => {
  const validStatuses: GameStatus[] = ['playing', 'won', 'lost'];
  return validStatuses.includes(status as GameStatus) ? status as GameStatus : 'playing';
};

// Create mode-aware game state getter/setter instead of global variable
const getCurrentModeAwareGameState = () => {
  const currentMode = (localStorage.getItem('babblegram_game_mode') as 'daily' | 'practice' | 'custom') || 'daily';
  const savedStatusRaw = modeAwareStorage.getGameStatus();
  const savedLevel = modeAwareStorage.getCurrentLevel() || 1;

  // DATE-AWARE: In DAILY mode, ignore stale status from previous day
  let status: GameStatus = validateGameStatus(savedStatusRaw) || 'playing';
  if (currentMode === 'daily') {
    try {
      const savedDate = modeAwareStorage.getPersistenceDate();
      const today = formatDate(getEffectiveDate());
      if (!savedDate || savedDate !== today) {
        status = 'playing';
      }
    } catch (e) {
      console.warn('getCurrentModeAwareGameState date check failed, defaulting to playing', e);
      status = 'playing';
    }
  }
  
  return {
    status,
    currentLevel: savedLevel,
    showCompletion: false,
    lastUpdate: new Date().toISOString()
  };
};

const setCurrentModeAwareGameState = (newState: any) => {
  modeAwareStorage.setGameStatus(newState.status);
  modeAwareStorage.setCurrentLevel(newState.currentLevel);
  if (newState.status === 'won' || newState.status === 'lost') {
    modeAwareStorage.setCookie('game_completion_state', 'true');
  }
};

const dispatchGameStatusUpdate = (status: GameStatus, showCompletion: boolean) => {
  if (typeof window === 'undefined') return;
  
  try {
    const event = new CustomEvent('gameStatusUpdate', { 
      detail: { 
        status,
        showCompletion,
        timestamp: new Date().toISOString()
      }
    });
    logStatusEvent('dispatch', { to: status, showCompletion });
    window.dispatchEvent(event);
  } catch (error) {
    console.error('Error dispatching game status update:', error);
  }
};

export const getCompletionMessage = (): string => {
  const effectiveStatus = getEffectiveGameStatus();
  const currentMode = localStorage.getItem('babblegram_game_mode') || 'daily';
  const isPracticeMode = currentMode === 'practice';
  
  console.log('Getting completion message:', {
    effectiveStatus,
    currentMode,
    isPracticeMode,
    timestamp: new Date().toISOString()
  });

  if (effectiveStatus === 'won') {
    return "Congratulations, you won!";
  } else if (effectiveStatus === 'lost') {
    return isPracticeMode ? "Hard Luck! Try Again" : "Hard Luck! Try again tomorrow!";
  }
  
  return "Game Complete";
};

export const getEffectiveGameStatus = (): GameStatus => {
  try {
    const currentMode = (localStorage.getItem('babblegram_game_mode') as 'daily' | 'practice' | 'custom') || 'daily';
    const savedStatus = validateGameStatus(modeAwareStorage.getGameStatus());

    // DATE-AWARE: In DAILY mode, ignore stale status from previous day
    if (currentMode === 'daily') {
      const savedDate = modeAwareStorage.getPersistenceDate();
      const today = formatDate(getEffectiveDate());
      if (!savedDate || savedDate !== today) {
        console.log('getEffectiveGameStatus: Ignoring stale daily status; returning playing', { savedDate, today, savedStatus });
        return 'playing';
      }
    }

    const currentState = getCurrentGameStatus();
    const effectiveStatus = savedStatus || currentState.status;

    console.log('Getting effective game status:', {
      savedStatus,
      currentStatus: currentState.status,
      effectiveStatus,
      timestamp: new Date().toISOString()
    });

    return validateGameStatus(effectiveStatus) || 'playing';
  } catch (error) {
    console.error('Error getting effective game status:', error);
    return 'playing';
  }
};

export const initializeGameStatus = async () => {
  try {
    const currentMode = localStorage.getItem('babblegram_game_mode') || 'daily';
    const savedLevel = modeAwareStorage.getCurrentLevel() || 1;

    // Use date-aware effective status for DAILY mode
    const effectiveStatus = getEffectiveGameStatus();
    
    const gameState = {
      status: validateGameStatus(effectiveStatus) || 'playing',
      currentLevel: savedLevel,
      showCompletion: false,
      lastUpdate: new Date().toISOString()
    };
    
    // Safety guard: in DAILY + debug date, prevent stale completion with no guesses for today
    try {
      const currentMode = (localStorage.getItem('babblegram_game_mode') as 'daily' | 'practice' | 'custom') || 'daily';
      if (currentMode === 'daily') {
        const today = formatDate(getEffectiveDate());
        const savedDate = modeAwareStorage.getPersistenceDate();
        const completionCookie = modeAwareStorage.getCookie('game_completion_state') === 'true';
        const guessHistory = modeAwareStorage.getGuessHistory();
        const hasLegacyGuesses = Array.isArray(guessHistory) && guessHistory.length > 0;
        const atomicState = atomicGameStorage.read('daily');
        const hasAtomicCompletion = Boolean(
          atomicState &&
          atomicState.persistenceDate === today &&
          (atomicState.gameStatus === 'won' || atomicState.gameStatus === 'lost' || atomicState.completionState)
        );

        if (
          (gameState.status === 'won' || gameState.status === 'lost') &&
          savedDate === today &&
          !completionCookie &&
          !hasLegacyGuesses &&
          !hasAtomicCompletion
        ) {
          console.log('Resetting stale daily completion with no valid persistence signals for today', {
            savedDate,
            today,
            hasLegacyGuesses,
            hasAtomicCompletion,
            completionCookie
          });
          modeAwareStorage.removeCookie('game_status');
          modeAwareStorage.removeCookie('game_completion_state');
          modeAwareStorage.removeCookie('game_persistence_date');
          gameState.status = 'playing';
        }
      }
    } catch (e) {
      console.warn('Failed stale-completion guard, continuing with computed status', e);
    }
    
    console.log('Initializing game status:', {
      currentMode,
      effectiveStatus,
      savedLevel,
      timestamp: new Date().toISOString()
    });
    
    // Check completion status for current mode
    gameState.showCompletion = shouldShowCompletion(currentMode);
    
    console.log('Game status initialized:', {
      currentMode,
      status: gameState.status,
      showCompletion: gameState.showCompletion,
      timestamp: new Date().toISOString()
    });
    logStatusEvent('initialize', { to: gameState.status, showCompletion: gameState.showCompletion });
    
    return gameState;
  } catch (error) {
    console.error('Error initializing game status:', error);
    // Return a safe default state
    return {
      status: 'playing',
      currentLevel: 1,
      showCompletion: false,
      lastUpdate: new Date().toISOString()
    };
  }
};

export const handleGameWinFinal = async (
  setGameStatus: (status: GameStatus) => void,
  setShowGameCompletion: (show: boolean) => void,
  currentLevel: number,
  isGameComplete: boolean = false
) => {
  try {
    console.log('🎉 GAME WIN HANDLER STARTED');
  } catch (e) {
    console.error('Even console.log failed:', e);
  }

  try {
    if (currentLevel === 4 || isGameComplete) {
      console.log('🎉 About to call getEffectiveDate...');
      const effectiveDate = getEffectiveDate();
      console.log('🎉 getEffectiveDate succeeded:', effectiveDate);
      const formattedDate = formatDate(effectiveDate);
      
      // First, update all completion-related cookies atomically
      // Mark game as completed using the centralized manager
      CompletionStateManager.markGameCompleted('won');
      
      await Promise.all([
        modeAwareStorage.setCurrentLevel(currentLevel),
        modeAwareStorage.setPersistenceDate(formattedDate)
      ]).catch(error => {
        console.error('Error updating cookies:', error);
        throw error;
      });

      // Update mode-aware state
      const gameState = {
        status: 'won' as GameStatus,
        currentLevel,
        showCompletion: true,
        lastUpdate: new Date().toISOString()
      };
      setCurrentModeAwareGameState(gameState);

      // Update UI state
      setGameStatus('won');
      setShowGameCompletion(true);

      // Only handle streak after all state is properly saved
      await streakManager.handleGameCompletion(true, currentLevel);
      
      // Update game status last to ensure all other states are saved
      await updateGameStatus('won', currentLevel, true, false);
      
      console.log('DEBUG: Game win handling complete:', {
        cookies: {
          gameStatus: modeAwareStorage.getGameStatus(),
          currentLevel: modeAwareStorage.getCurrentLevel(),
          completionState: modeAwareStorage.getCookie('game_completion_state'),
          persistenceDate: modeAwareStorage.getPersistenceDate()
        },
        completionManagerState: CompletionStateManager.hasCompletedGame(localStorage.getItem('babblegram_game_mode') as any || 'daily'),
        showCompletion: true,
        timestamp: new Date().toISOString()
      });
    } else {
      setGameStatus('won');
      setShowGameCompletion(false);
      await updateGameStatus('won', currentLevel, false);
    }
    
    logStatusEvent('win-final', { to: 'won', currentLevel, isGameComplete });
    return getCurrentModeAwareGameState();
  } catch (error) {
    console.error('Error handling game win:', error);
    throw error;
  }
};

export const handleGameLoss = async (
  setGameStatus: (status: GameStatus) => void,
  setShowGameCompletion: (show: boolean) => void,
  currentLevel?: number
) => {
  console.log('Handling game loss:', {
    currentLevel,
    timestamp: new Date().toISOString()
  });

  try {
    const effectiveDate = getEffectiveDate();
    const formattedDate = formatDate(effectiveDate);
    
    // Update mode-aware state first
    const gameState = {
      status: 'lost' as GameStatus,
      currentLevel,
      showCompletion: false,
      lastUpdate: new Date().toISOString()
    };
    setCurrentModeAwareGameState(gameState);

    // Update UI state
    setGameStatus('lost');

    // Set failed level if provided
    if (currentLevel) {
      modeAwareStorage.setFailedLevel(currentLevel);
    }

    // Important: Handle streak reset BEFORE updating game status
    // This ensures the streak is reset even if subsequent operations fail
    await streakManager.handleGameCompletion(false, currentLevel || 1);

    // Mark game as completed using the centralized manager
    CompletionStateManager.markGameCompleted('lost');
    
    // Then update all game state
    await Promise.all([
      updateGameStatus('lost', currentLevel, false, true),
      currentLevel ? modeAwareStorage.setCurrentLevel(currentLevel) : Promise.resolve(),
      modeAwareStorage.setPersistenceDate(formattedDate)
    ]).catch(error => {
      console.error('Error updating cookies:', error);
    });
    
    logStatusEvent('loss', { to: 'lost', currentLevel });
    return getCurrentModeAwareGameState();
  } catch (error) {
    console.error('Error handling game loss:', error);
    throw error;
  }
};

export const updateGameStatus = async (
  status: GameStatus,
  level?: number,
  forceShowCompletion?: boolean,
  skipPersistence: boolean = false
) => {
  try {
    const currentState = getCurrentModeAwareGameState();
    const previousStatus = currentState.status;
    
    console.log('Game status update:', {
      from: previousStatus,
      to: status,
      level,
      skipPersistence,
      timestamp: new Date().toISOString()
    });
    logStatusEvent('update', { from: previousStatus, to: status, level, skipPersistence });

    // Update current game state atomically
    const updatedState = {
      status,
      currentLevel: level || currentState.currentLevel,
      lastUpdate: new Date().toISOString(),
      showCompletion: forceShowCompletion !== undefined ? forceShowCompletion : shouldShowCompletion()
    };
    
    // Set new state
    setCurrentModeAwareGameState(updatedState);

    if (!skipPersistence) {
      try {
        const formattedDate = formatDate(getEffectiveDate());
        // Update cookies after state is set
        await Promise.all([
          modeAwareStorage.setGameStatus(status),
          level ? modeAwareStorage.setCurrentLevel(level) : Promise.resolve(),
          modeAwareStorage.setPersistenceDate(formattedDate),
          modeAwareStorage.setLastPlayed(formattedDate)
        ]);
        
        // Explicitly set game_completion_state cookie for won/lost games
        if (status === 'won' || status === 'lost') {
          modeAwareStorage.setCookie('game_completion_state', 'true');
        }
        
        // Dispatch event after cookies are updated
        dispatchGameStatusUpdate(status, updatedState.showCompletion);
      } catch (error) {
        console.error('Error persisting game status:', error);
      }
    }
    
    return getCurrentModeAwareGameState();
  } catch (error) {
    console.error('Error updating game status:', error);
    return getCurrentModeAwareGameState();
  }
};

export const getCurrentGameStatus = () => {
  try {
    const currentState = getCurrentModeAwareGameState();
    return {
      ...currentState,
      showCompletion: shouldShowCompletion()
    };
  } catch (error) {
    console.error('Error getting current game status:', error);
    return {
      status: 'playing',
      currentLevel: 1,
      showCompletion: false,
      lastUpdate: new Date().toISOString()
    };
  }
};

// validateGameStatus moved to top of file

export const subscribeToGameStatus = (
  callback: (status: any) => void
) => {
  const handleStatusUpdate = (event: CustomEvent) => {
    try {
      const currentState = getCurrentModeAwareGameState();
      callback({
        ...currentState,
        ...event.detail
      });
    } catch (error) {
      console.error('Error in game status subscription callback:', error);
    }
  };

  window.addEventListener('gameStatusUpdate', handleStatusUpdate as EventListener);
  
  return () => {
    window.removeEventListener('gameStatusUpdate', handleStatusUpdate as EventListener);
  };
};

export const shouldShowCompletion = (mode?: string): boolean => {
  try {
    // Use the centralized completion state manager
    const targetMode = mode as any || (localStorage.getItem('babblegram_game_mode') as any) || 'daily';
    const shouldShow = CompletionStateManager.shouldShowCompletion(targetMode);
    
    console.log('Checking should show completion:', {
      targetMode,
      shouldShow,
      timestamp: new Date().toISOString()
    });
    
    return shouldShow;
  } catch (error) {
    console.error('Error in shouldShowCompletion:', error);
    return false;
  }
};

export const clearGamePersistence = () => {
  try {
    modeAwareStorage.setPersistenceDate('');
    modeAwareStorage.setCookie('game_completion_state', '');
  } catch (error) {
    console.error('Error clearing game persistence:', error);
  }
};
